/**
 * 浏览器兼容性检测
 * 支持浏览器：Chrome、Edge、Safari、360浏览器、QQ浏览器
 * 暂不支持：夸克浏览器
 */

export interface BrowserInfo {
  name: string;
  version: number;
  key: string;
}

/** 最低支持版本（未列入或 key 未知 → 不支持） */
const MIN_VERSIONS: Record<string, number> = {
  chrome: 120,
  edge: 149,
  safari: 17,
  "360": 16,
  qq: 21,
};

function isVersionSupported(key: string, version: number): boolean {
  const min = MIN_VERSIONS[key];
  if (min === undefined) return false;
  if (min === 0) return true;
  return version >= min;
}

/**
 * 解析 userAgent 获取浏览器名称和版本
 * 检测顺序很重要：国内浏览器壳 > Edge > Chrome > Safari
 */
export function parseBrowser(): BrowserInfo {
  const ua = navigator.userAgent;

  // QQ浏览器（Chromium 壳，优先检测）
  const qq = ua.match(/QQBrowser\/(\d+)/i);
  if (qq) return { name: "QQ浏览器", version: parseInt(qq[1], 10), key: "qq" };

  // 夸克浏览器（暂不支持，需单独识别，避免误判为 Chrome）
  const quark = ua.match(/QuarkPC\/(\d+)/i);
  if (quark) return { name: "夸克浏览器", version: parseInt(quark[1], 10), key: "QuarkPC" };

  // 360浏览器（版本号如 16.1.1098.64，取主版本.次版本）
  // 常见 UA 特征：360SE、360EE、360Browser、QIHU 360SE 等
  const m360 = ua.match(/360(?:SE|EE|Browser)\s*\/\s*(\d+(?:\.\d+)?)/i);
  if (m360) return { name: "360浏览器", version: parseFloat(m360[1]), key: "360" };
  // 无版本号的兜底：只要 UA 中出现 360 浏览器特征就识别
  if (/360(?:SE|EE|Browser|安全浏览器|极速浏览器)|QIHU/i.test(ua)) return { name: "360浏览器", version: 0, key: "360" };

  const uaL = ua.toLowerCase();

  // Edge (Chromium) — 必须在 Chrome 前
  const edg = uaL.match(/edg\/(\d+)/);
  if (edg) return { name: "Edge", version: parseInt(edg[1], 10), key: "edge" };

  // Chrome（排除已被上面命中的壳浏览器）
  const chrome = uaL.match(/chrome\/(\d+)/);
  if (chrome) return { name: "Chrome", version: parseInt(chrome[1], 10), key: "chrome" };

  // Safari（不含 Chrome 的才是真 Safari）
  const safari = uaL.match(/version\/(\d+)/);
  if (safari && uaL.includes("safari") && !uaL.includes("chrome"))
    return { name: "Safari", version: parseInt(safari[1], 10), key: "safari" };

  return { name: "Unknown", version: 0, key: "unknown" };
}

/**
 * 检测当前浏览器是否受支持（名称 + 版本都要满足）
 */
export function isBrowserSupported(): boolean {
  const info = parseBrowser();
  return isVersionSupported(info.key, info.version);
}
